/**
 * OCPP 2.0.1 V2G / ISO 15118-adjacent CP→CSMS notifications:
 *   - NotifyEVChargingNeeds   — the EV's requested energy-transfer needs.
 *   - NotifyEVChargingSchedule — the schedule the CP/EV negotiated.
 *
 * These are part of the CP-initiated capability surface; the simulator models
 * no EV-side negotiation, so there is no automatic trigger (mirrors
 * NotifyChargingLimit). The builders construct schema-valid wire frames.
 */
import type { ChargingRateUnit } from '@/domain/types';

export type EnergyTransferMode = 'DC' | 'AC_single_phase' | 'AC_two_phase' | 'AC_three_phase';

export interface NotifyEVChargingNeedsDomain {
  readonly evseId: number;
  readonly requestedEnergyTransfer: EnergyTransferMode;
  readonly departureTime?: string;
  readonly maxScheduleTuples?: number;
}

export function buildNotifyEVChargingNeedsRequest(
  req: NotifyEVChargingNeedsDomain,
): Record<string, unknown> {
  return {
    evseId: req.evseId,
    ...(req.maxScheduleTuples !== undefined ? { maxScheduleTuples: req.maxScheduleTuples } : {}),
    chargingNeeds: {
      requestedEnergyTransfer: req.requestedEnergyTransfer,
      ...(req.departureTime !== undefined ? { departureTime: req.departureTime } : {}),
    },
  };
}

export interface EVSchedulePeriod {
  readonly startPeriod: number;
  readonly limit: number;
  readonly numberPhases?: number;
}

export interface NotifyEVChargingScheduleDomain {
  readonly evseId: number;
  /** ISO timestamp the schedule's startPeriod offsets are relative to. */
  readonly timeBase: string;
  readonly scheduleId: number;
  readonly chargingRateUnit: ChargingRateUnit;
  readonly periods: readonly EVSchedulePeriod[];
  readonly duration?: number;
}

export function buildNotifyEVChargingScheduleRequest(
  req: NotifyEVChargingScheduleDomain,
): Record<string, unknown> {
  return {
    timeBase: req.timeBase,
    evseId: req.evseId,
    chargingSchedule: {
      id: req.scheduleId,
      chargingRateUnit: req.chargingRateUnit,
      ...(req.duration !== undefined ? { duration: req.duration } : {}),
      chargingSchedulePeriod: req.periods.map((p) => ({
        startPeriod: p.startPeriod,
        limit: p.limit,
        ...(p.numberPhases !== undefined ? { numberPhases: p.numberPhases } : {}),
      })),
    },
  };
}
